import { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type { Company, Group, Role, User } from '@esg/esg-global-types';

import { UserContext } from '../../context/UserContext';
import { checkUserGroupAdmin } from '../../util/user_access';

interface EditUserAccessTabProps {
  draft: User;
  groups: Group[];
  roles: Role[];
  onChange: (patch: Partial<User>) => void;
}

const GROUP_ADMIN_TOOLTIP = 'Only super admins can grant or revoke group-admin access.';

/**
 * Manages a user's group membership and company access. The top section adds or
 * removes whole-group membership and toggles group-admin status (super-admin
 * only); the lower section grants per-(group, company, role) access with a
 * remove button and an inline "Add company access" picker. Custom-role
 * permissions are configured on the Permissions tab.
 */
const EditUserAccessTab = ({ draft, groups, roles, onChange }: EditUserAccessTabProps) => {
  const { user: currentUser } = useContext(UserContext);
  // Granting/revoking either super-admin or group-admin is restricted to super
  // admins. A non-super group admin never sees the super-admin toggle at all
  // (nor can they escalate a user to super admin).
  const isSuperAdmin = !!currentUser?.super_admin;
  const canManageGroupAdmin = isSuperAdmin;

  // Scope the target user's group/company access to what the signed-in viewer
  // administers. The API returns the target's full `groups` map (reads are not
  // scoped server-side), so a non-super group admin must not see — or be able
  // to act on — memberships in groups they don't administer. Mirrors the
  // table's CompanyChipStack scoping; a super admin sees everything.
  const canManageGroup = (groupId: string): boolean =>
    isSuperAdmin || (!!currentUser && checkUserGroupAdmin(currentUser, groupId));

  const [addMembershipGroupId, setAddMembershipGroupId] = useState<string>('');
  const [addGroupId, setAddGroupId] = useState<string>('');
  const [addCompanyId, setAddCompanyId] = useState<string>('');
  const [addRole, setAddRole] = useState<string>(
    roles.find((r) => r.name.toLowerCase() === 'viewer')?.name.toLowerCase() ??
      roles[0]?.name.toLowerCase() ??
      'viewer'
  );
  const companyLookup = useMemo(() => {
    const map = new Map<string, { name: string; group_name: string }>();
    for (const g of groups) {
      for (const c of g.companies ?? []) {
        map.set(c.id, { name: c.name, group_name: g.name });
      }
    }
    return map;
  }, [groups]);

  const groupMembershipRows: Array<{
    group_id: string;
    group_name: string;
    is_admin: boolean;
    company_count: number;
  }> = useMemo(
    () =>
      Object.keys(draft.groups ?? {})
        .filter((groupId) => canManageGroup(groupId))
        .map((groupId) => ({
          group_id: groupId,
          group_name: groups.find((g) => g.id === groupId)?.name ?? groupId,
          is_admin: !!draft.groups?.[groupId]?.admin,
          company_count: Object.keys(draft.groups?.[groupId]?.companies ?? {}).length
        })),
    [draft.groups, groups, currentUser, isSuperAdmin]
  );

  const joinableGroups: Group[] = useMemo(
    () => groups.filter((g) => !draft.groups?.[g.id]),
    [groups, draft.groups]
  );

  const accessRows: Array<{
    group_id: string;
    group_name: string;
    company_id: string;
    company_name: string;
    role: string;
  }> = useMemo(() => {
    const rows: typeof accessRows = [];
    for (const [groupId, groupData] of Object.entries(draft.groups ?? {})) {
      if (!canManageGroup(groupId)) continue;
      const groupName = groups.find((g) => g.id === groupId)?.name ?? groupId;
      for (const [companyId, companyData] of Object.entries(groupData.companies ?? {})) {
        rows.push({
          group_id: groupId,
          group_name: groupName,
          company_id: companyId,
          company_name: companyLookup.get(companyId)?.name ?? companyId,
          role: companyData.role
        });
      }
    }
    return rows;
  }, [draft.groups, groups, companyLookup, currentUser, isSuperAdmin]);

  const setGroupAdmin = (group_id: string, is_admin: boolean) => {
    const groupsPatch = { ...(draft.groups ?? {}) };
    const existing = groupsPatch[group_id] ?? { admin: false, companies: {} };
    groupsPatch[group_id] = { ...existing, admin: is_admin };
    onChange({ groups: groupsPatch });
  };

  const addToGroup = () => {
    if (!addMembershipGroupId || draft.groups?.[addMembershipGroupId]) return;
    const groupsPatch = { ...(draft.groups ?? {}) };
    groupsPatch[addMembershipGroupId] = { admin: false, companies: {} };
    onChange({ groups: groupsPatch });
    setAddMembershipGroupId('');
  };

  const removeFromGroup = (group_id: string) => {
    const groupsPatch = { ...(draft.groups ?? {}) };
    delete groupsPatch[group_id];
    onChange({ groups: groupsPatch });
  };

  const removeCompanyAccess = (group_id: string, company_id: string) => {
    const groupsPatch = { ...(draft.groups ?? {}) };
    const group = { ...groupsPatch[group_id] };
    const companies = { ...(group.companies ?? {}) };
    delete companies[company_id];
    group.companies = companies;
    groupsPatch[group_id] = group;
    onChange({ groups: groupsPatch });
  };

  const assignableCompanies: Company[] = useMemo(() => {
    if (!addGroupId) return [];
    const group = groups.find((g) => g.id === addGroupId);
    const existing = new Set(Object.keys(draft.groups?.[addGroupId]?.companies ?? {}));
    return (group?.companies ?? []).filter((c) => !existing.has(c.id));
  }, [addGroupId, groups, draft.groups]);

  const addCompanyAccess = () => {
    if (!addGroupId || !addCompanyId) return;
    const groupsPatch = { ...(draft.groups ?? {}) };
    const group = { ...(groupsPatch[addGroupId] ?? {}) };
    const companies = { ...(group.companies ?? {}) };
    companies[addCompanyId] = { role: addRole };
    group.companies = companies;
    groupsPatch[addGroupId] = group;
    onChange({ groups: groupsPatch });
    setAddCompanyId('');
  };

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      {isSuperAdmin && (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            Group administration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!!draft.super_admin}
                onChange={(e) => onChange({ super_admin: e.target.checked })}
              />
            }
            label="Super admin (full system access)"
          />
        </>
      )}

      {draft.super_admin ? (
        <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
          Super admins have access to every group and company, so individual group and company
          access is not configured here.
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 2 }}>
            Group membership ({groupMembershipRows.length})
          </Typography>
          <TableContainer
            component={Box}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>Group admin</TableCell>
                  <TableCell>Companies</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {groupMembershipRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
                        Not a member of any group.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupMembershipRows.map((row) => (
                    <TableRow key={row.group_id}>
                      <TableCell>{row.group_name}</TableCell>
                      <TableCell>
                        <Tooltip title={canManageGroupAdmin ? '' : GROUP_ADMIN_TOOLTIP}>
                          <span>
                            <Switch
                              size="small"
                              checked={row.is_admin}
                              disabled={!canManageGroupAdmin}
                              onChange={(e) => setGroupAdmin(row.group_id, e.target.checked)}
                              inputProps={{ 'aria-label': `Group admin for ${row.group_name}` }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {row.company_count === 0 ? (
                          <Typography variant="body2" color="text.disabled">
                            None
                          </Typography>
                        ) : (
                          <Chip size="small" label={row.company_count} variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell padding="none">
                        <Tooltip title="Remove from group">
                          <IconButton
                            size="small"
                            onClick={() => removeFromGroup(row.group_id)}
                            aria-label={`Remove from ${row.group_name}`}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {joinableGroups.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Add to group"
                value={addMembershipGroupId}
                onChange={(e) => setAddMembershipGroupId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="" disabled>
                  Select a group
                </MenuItem>
                {joinableGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addToGroup}
                disabled={!addMembershipGroupId}
                sx={{ mt: 0.25 }}
              >
                Add
              </Button>
            </Box>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 2 }}>
            Company access ({accessRows.length})
          </Typography>
          <TableContainer
            component={Box}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Group</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {accessRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
                        No company access assigned.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accessRows.map((row) => (
                    <TableRow key={`${row.group_id}-${row.company_id}`}>
                      <TableCell>{row.group_name}</TableCell>
                      <TableCell>{row.company_name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.role.charAt(0).toUpperCase() + row.role.slice(1)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell padding="none">
                        <IconButton
                          size="small"
                          onClick={() => removeCompanyAccess(row.group_id, row.company_id)}
                          aria-label="Remove access"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 1 }}>
            Add company access
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              flexWrap: 'wrap'
            }}
          >
            <TextField
              select
              size="small"
              label="Group"
              value={addGroupId}
              onChange={(e) => {
                setAddGroupId(e.target.value);
                setAddCompanyId('');
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="" disabled>
                Select a group
              </MenuItem>
              {groups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Company"
              value={addCompanyId}
              onChange={(e) => setAddCompanyId(e.target.value)}
              disabled={!addGroupId || assignableCompanies.length === 0}
              helperText={
                addGroupId && assignableCompanies.length === 0
                  ? 'All companies in this group are already assigned.'
                  : undefined
              }
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="" disabled>
                Select a company
              </MenuItem>
              {assignableCompanies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Role"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              {roles.map((r) => (
                <MenuItem key={r.id} value={r.name.toLowerCase()}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addCompanyAccess}
              disabled={!addGroupId || !addCompanyId}
              sx={{ mt: 0.25 }}
            >
              Add
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default EditUserAccessTab;
